
import { Position } from "@/types/locationTypes";

/**
 * Calculates the distance between two GPS coordinates using the Haversine formula
 * @param pos1 Start position
 * @param pos2 End position
 * @returns Distance in meters
 */
export const calculateDistance = (pos1: Position, pos2: Position): number => {
  const R = 6371e3; // Earth radius in meters
  const φ1 = (pos1.latitude * Math.PI) / 180;
  const φ2 = (pos2.latitude * Math.PI) / 180;
  const Δφ = ((pos2.latitude - pos1.latitude) * Math.PI) / 180;
  const Δλ = ((pos2.longitude - pos1.longitude) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // Distance in meters

  return d;
};

/**
 * Formats GPS coordinates for display
 * @param position Position object or null
 * @returns Formatted string representation of coordinates
 */
export const formatCoordinates = (position: Position | null): string => {
  if (!position) return "Non disponible";
  return `${position.latitude.toFixed(6)}, ${position.longitude.toFixed(6)}`;
};

/**
 * Formats a timestamp to a readable date and time string
 * @param timestamp Timestamp in milliseconds
 * @returns Formatted time string
 */
export const formatTimestamp = (timestamp: number | undefined): string => {
  if (!timestamp) return "Non disponible";
  
  const date = new Date(timestamp);
  
  // Format: HH:MM:SS
  return date.toLocaleTimeString('fr-FR', { 
    hour: '2-digit', 
    minute: '2-digit',
    second: '2-digit'
  });
};
